// Post-checkout landing page (used by Paddle's successUrl).
// Webhook is the source of truth — this page just polls credits and bounces back.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { Button } from "@/components/ui/button";
import { StitchPage, StitchPanel } from "@/components/stitch";
import { SEO } from "@/components/SEO";

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { refresh } = useCredits();
  const [polling, setPolling] = useState(true);

  // SEO handled by <SEO noindex /> component below.

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 8; i++) {
        if (cancelled) return;
        await refresh();
        await new Promise((r) => setTimeout(r, 1800));
      }
      if (!cancelled) setPolling(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  return (
    <>
      <SEO noindex />
    <StitchPage>
      <div className="flex min-h-screen items-center justify-center px-5">
        <StitchPanel className="w-full max-w-sm p-7 text-center" glow>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success shadow-[0_0_30px_-6px_hsl(var(--success))]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary/80">
            — TRANSMISSION RECEIVED
          </p>
          <h1 className="font-display mt-2 text-2xl font-bold uppercase tracking-tight text-foreground">
            Payment received
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {polling
              ? "Adding credits to your account…"
              : "Your credits are ready. Open the app to start generating."}
          </p>
          {polling && (
            <Loader2 className="mx-auto mt-4 h-5 w-5 animate-spin text-primary" />
          )}
          <div className="mt-6 flex flex-col gap-2">
            <Button
              onClick={() => navigate("/app")}
              className="font-display w-full bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_30px_-6px_hsl(var(--primary))]"
            >
              Back to the app
            </Button>
            <Button variant="ghost" onClick={() => navigate("/app/profile")} className="w-full text-xs">
              View balance
            </Button>
          </div>
        </StitchPanel>
      </div>
    </StitchPage>
    </>
  );
};

export default CheckoutSuccess;
